import auth0 from 'auth0-js';
import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';

import { ACCESS_TOKEN_LS_KEY, EXPIRES_AT_LS_KEY } from '../../constants/shared-constants';

import type { AuthConfig, AuthContextData, AuthData } from './AuthProvider.types';

const AuthContext = createContext<AuthContextData | undefined>(undefined);

export function AuthProvider({ children, config }: { children: ReactNode; config: AuthConfig }) {
  const [error, setError] = useState<any>(null);
  const [codeLoading, setCodeLoading] = useState(false);

  const auth0Client = useMemo(() => {
    return new auth0.WebAuth({
      domain: config.domain,
      clientID: config.clientID,
      audience: config.audience,
      responseType: 'token id_token',
      redirectUri: config.redirectUri,
    });
  }, [config]);

  const setAuthData = (authResult: AuthData) => {
    const expiresAt = JSON.stringify(authResult.expiresIn * 1000 + new Date().getTime());
    localStorage.setItem(ACCESS_TOKEN_LS_KEY, authResult.accessToken);
    localStorage.setItem(EXPIRES_AT_LS_KEY, expiresAt);
    localStorage.setItem('id_token2', 'dfdf');
  };

  const clearAuthData = () => {
    localStorage.removeItem(ACCESS_TOKEN_LS_KEY);
    localStorage.removeItem(EXPIRES_AT_LS_KEY);
  };

  const isAuthenticated = useCallback(() => {
    try {
      const expiresAt = JSON.parse(localStorage.getItem(EXPIRES_AT_LS_KEY) ?? '0');
      return new Date().getTime() < expiresAt;
    } catch {
      return false;
    }
  }, []);

  const signOut = useCallback(
    async (reload = false) => {
      clearAuthData();
      window.location.hash = '';
      auth0Client.logout({
        returnTo: `${window.location.origin}`,
        clientID: config.clientID,
      });

      reload && window.location.reload();
    },
    [auth0Client, config.clientID],
  );

  const sendSmsCode = useCallback(
    async (phone: string) => {
      setCodeLoading(true);
      return new Promise((resolve, reject) => {
        auth0Client.passwordlessStart(
          {
            connection: 'sms',
            send: 'code',
            phoneNumber: phone,
          },
          function (err, res) {
            setCodeLoading(false);
            if (err) {
              setError(err);
              reject(err);
              return;
            }
            if (res) {
              console.info('Response: ' + JSON.stringify(res));
              console.info('Response: ' + JSON.stringify(res.status));
              resolve(res);
            }
          },
        );
      });
    },
    [auth0Client],
  );

  const sendEmailCode = useCallback(
    async (email: string) => {
      setCodeLoading(true);
      return new Promise((resolve, reject) => {
        auth0Client.passwordlessStart(
          {
            connection: 'email',
            send: 'code',
            email: email,
          },
          function (err, res) {
            setCodeLoading(false);
            if (err) {
              setError(err);
              reject(err);
              return;
            }
            if (res) {
              console.info('Response: ' + JSON.stringify(res));
              console.info('Response: ' + JSON.stringify(res.status));
              resolve(res);
            }
          },
        );
      });
    },
    [auth0Client],
  );

  const parseAuthToken = useCallback(async () => {
    return new Promise(resolve => {
      if (window.location.hash) {
        auth0Client.parseHash((err, authResult) => {
          if (authResult?.accessToken) {
            setAuthData(authResult as AuthData);
            auth0Client.client.userInfo(authResult.accessToken, (err, res) => {
              if (err) {
                console.error('parseAuthToken error', err);
                return null;
              }
              resolve(res);
            });
            resolve(authResult.state);
          }
          if (err) {
            console.error('login callback error parsing hash', err);
            return null;
          }
        });
      }
    });
  }, [auth0Client]);

  const verifySmsCode = useCallback(
    async (email: string, verificationCode: string, state?: string, nonce?: string) => {
      return new Promise((resolve, reject) => {
        auth0Client.passwordlessLogin(
          {
            connection: 'sms',
            phoneNumber: email,
            verificationCode: verificationCode,
            state: state || '',
            nonce: nonce || '',
          },
          (err, res) => {
            if (err) {
              setError(err);
              reject(err);
              return;
            }
            if (res) {
              console.warn('code response', res);
              resolve(res);
            }
          },
        );
      });
    },
    [auth0Client],
  );

  const verifyEmailCode = useCallback(
    async (email: string, verificationCode: string, state?: string, nonce?: string) => {
      return new Promise((resolve, reject) => {
        auth0Client.passwordlessLogin(
          {
            connection: 'email',
            email: email,
            verificationCode: verificationCode,
            state: state || '',
            nonce: nonce || '',
          },
          (err, res) => {
            if (err) {
              setError(err);
              reject(err);
              return;
            }
            if (res) {
              console.warn('code response', res);
              resolve(res);
            }
          },
        );
      });
    },
    [auth0Client],
  );

  const authState = useMemo(
    () => ({
      sendSmsCode,
      sendEmailCode,
      signOut,
      error,
      verifySmsCode,
      verifyEmailCode,
      parseAuthToken,
      isAuthenticated,
      codeLoading,
    }),
    [
      sendSmsCode,
      sendEmailCode,
      signOut,
      error,
      verifySmsCode,
      verifyEmailCode,
      parseAuthToken,
      isAuthenticated,
      codeLoading,
    ],
  );

  return <AuthContext.Provider value={authState}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
